import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import './ControlSortList.scss';
import { ControlSortListFilters } from '../ControlSortListFilters/ControlSortListFilters';
import {
  HubSortListEntry,
  HubSortListOrderEntry,
} from '../../hooks/useFetchHubSortList';
import { TabsComponent } from '../../ReusableComponents/Tabs/TabsComponent';
import { AutoSizer, CellMeasurerCache, List } from 'react-virtualized';
import { useDeepCompareMemo } from 'use-deep-compare';
import { SortListTable } from './SortListTable';
import { isEqual, debounce } from 'lodash';
import { Region } from '../../types/orderTypes';

let cacheForIncomplete: CellMeasurerCache;
let cacheForComplete: CellMeasurerCache;

let listRefComplete: List;
let listRefIncomplete: List;

export function totalNumberOfPackages(bundle: HubSortListOrderEntry[]): number {
  return bundle.reduce((acc, cur) => {
    return acc + cur.order.packagesNumber;
  }, 0);
}

function isNotCompleted(
  scannedPackages: number,
  packagesNumber: number
): boolean {
  return !(scannedPackages === packagesNumber && packagesNumber > 0);
}

declare interface ControlSortListParameters {
  scanningItems: HubSortListEntry[];
  updateHubSortListEntries?: (bundleId: string[]) => Promise<void>;
  region?: Region;
}

interface SerializedSortListBundles {
  bundlesWithIssues: SortListBundles[];
  bundlesCompleted: SortListBundles[];
}

interface SerializedFilteredSortListBundles {
  filteredBundlesWithIssues: SortListBundles[];
  filteredBundlesCompleted: SortListBundles[];
}

export interface SortListBundles {
  listEntry: HubSortListEntry;
  bundleOrders: HubSortListOrderEntry[];
}

const checkProps = (
  prevProps: Readonly<ControlSortListParameters>,
  nextProps: Readonly<ControlSortListParameters>
) => isEqual(prevProps.scanningItems, nextProps.scanningItems);

const ControlSortList = memo(
  ({
    scanningItems = [],
    updateHubSortListEntries,
  }: ControlSortListParameters): JSX.Element => {
    const filterSectionRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [filteredBundles, setFilteredBundles] =
      useState<SerializedFilteredSortListBundles>({
        filteredBundlesWithIssues: null,
        filteredBundlesCompleted: null,
      });
    const senderNames = [].concat(
      ...scanningItems.map(scanningItem => {
        return scanningItem.orders.map(item => item.order.senderName);
      })
    );
    const allSenders = new Set(senderNames);
    const [selectedSenders, setSelectedSenders] =
      useState<Set<string>>(allSenders);
    const [selectedTab, setSelectedTab] = useState<string>('incomplete');
    const [sortListBundles, setSortListBundles] =
      useState<SerializedSortListBundles>({
        bundlesWithIssues: [],
        bundlesCompleted: [],
      });
    const searcherListener = event => {
      if (
        (event.key === 'f' || event.key === 'F') &&
        (event.ctrlKey || event.metaKey)
      ) {
        event.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };
    useEffect(() => {
      window.addEventListener('keydown', searcherListener);
      cacheForIncomplete = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 50,
      });

      cacheForComplete = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 50,
      });
      return function clearListCache() {
        cacheForIncomplete = null;
        cacheForComplete = null;
        window.removeEventListener('keydown', searcherListener);
      };
    }, []);

    useEffect(() => {
      if (
        selectedTab === 'incomplete' &&
        cacheForIncomplete &&
        listRefIncomplete
      ) {
        cacheForIncomplete.clearAll();
        listRefIncomplete.recomputeRowHeights();
      } else if (
        selectedTab === 'complete' &&
        cacheForComplete &&
        listRefComplete
      ) {
        cacheForComplete.clearAll();
        listRefComplete.recomputeRowHeights();
      }
    }, [selectedTab]);

    useEffect(() => {
      const bundlesWithIssues = [];
      const bundlesCompleted = [];

      scanningItems.forEach(item => {
        const bundleOrders = item.orders.filter(
          orderEntry =>
            selectedSenders.has(orderEntry.order.senderName) &&
            isNotCompleted(
              orderEntry.scannedParcelCount,
              orderEntry.preciseParcelCount
            )
        );

        if (bundleOrders.length > 0) {
          const remaining = item.orders.filter(
            hubSortEntry =>
              hubSortEntry.order.status !== 'canceled' &&
              hubSortEntry.scannedParcelCount === 0
          );

          if (remaining.length > 0) {
            bundlesWithIssues.push({
              listEntry: item,
              bundleOrders,
            });
          } else {
            bundlesCompleted.push({
              listEntry: item,
              bundleOrders,
            });
          }
        }
      });
      setSortListBundles({ bundlesCompleted, bundlesWithIssues });
    }, [scanningItems, selectedSenders]);

    const debouncedFilterAction = useCallback(
      debounce((inputValue: string) => {
        if (inputValue) {
          const lowerCaseInput = inputValue.toLowerCase();
          setFilteredBundles(() => {
            const filteredBundlesWithIssues =
              sortListBundles.bundlesWithIssues.filter(
                bundle =>
                  bundle.listEntry.shelfId
                    ?.toLowerCase()
                    .includes(lowerCaseInput) ||
                  bundle.listEntry.courierName
                    ?.toLowerCase()
                    .includes(lowerCaseInput) ||
                  !!bundle.bundleOrders.find(hubOrder =>
                    hubOrder.order.id.includes(lowerCaseInput)
                  )
              );
            const filteredBundlesCompleted =
              sortListBundles.bundlesCompleted.filter(
                bundle =>
                  bundle.listEntry.shelfId
                    ?.toLowerCase()
                    .includes(lowerCaseInput) ||
                  bundle.listEntry.courierName
                    ?.toLowerCase()
                    .includes(lowerCaseInput) ||
                  !!bundle.bundleOrders.find(hubOrder =>
                    hubOrder.order.id.includes(lowerCaseInput)
                  )
              );
            return { filteredBundlesCompleted, filteredBundlesWithIssues };
          });
        } else {
          setFilteredBundles({
            filteredBundlesWithIssues: null,
            filteredBundlesCompleted: null,
          });
        }
      }, 500),
      [sortListBundles]
    );

    const onChangeInput = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(event.currentTarget.value);
        debouncedFilterAction(event.currentTarget.value);
      },
      [debouncedFilterAction]
    );

    const handleCheckboxChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const senderName = event.currentTarget.name;
        const updatedSenders = new Set(selectedSenders);
        if (!selectedSenders.has(senderName)) {
          updatedSenders.add(senderName);
          setSelectedSenders(updatedSenders);
        } else {
          updatedSenders.delete(senderName);
          setSelectedSenders(updatedSenders);
        }
      },
      [selectedSenders]
    );

    const sortListIncompleteTableWrapper = useDeepCompareMemo(
      () =>
        ({ index, key, style }) => {
          const sortListBundle =
            filteredBundles.filteredBundlesWithIssues &&
            filteredBundles.filteredBundlesWithIssues.length
              ? filteredBundles.filteredBundlesWithIssues[index]
              : sortListBundles.bundlesWithIssues[index];
          if (sortListBundle) {
            const { listEntry, bundleOrders } = sortListBundle;
            return (
              <div key={key} style={style}>
                <SortListTable
                  bundleId={listEntry.bundleId}
                  shelfId={listEntry.shelfId}
                  allBundleOrders={listEntry.orders}
                  courierName={listEntry.courierName}
                  bundleOrders={bundleOrders}
                  updateHubSortListEntries={updateHubSortListEntries}
                  cache={cacheForIncomplete}
                  listRef={listRefIncomplete}
                  virtualizationIndex={index}
                  onlyRemaining
                />
              </div>
            );
          } else {
            return <div key={key} style={style} />;
          }
        },
      [
        sortListBundles.bundlesWithIssues,
        updateHubSortListEntries,
        cacheForIncomplete,
        filteredBundles,
      ]
    );

    const sortListCompleteTableWrapper = useDeepCompareMemo(
      () =>
        ({ index, key, style }) => {
          const sortListBundle =
            filteredBundles.filteredBundlesCompleted &&
            filteredBundles.filteredBundlesCompleted.length
              ? filteredBundles.filteredBundlesCompleted[index]
              : sortListBundles.bundlesCompleted[index];
          if (sortListBundle) {
            const { listEntry, bundleOrders } = sortListBundle;
            return (
              <div key={key} style={style}>
                <SortListTable
                  bundleId={listEntry.bundleId}
                  shelfId={listEntry.shelfId}
                  allBundleOrders={listEntry.orders}
                  courierName={listEntry.courierName}
                  bundleOrders={bundleOrders}
                  updateHubSortListEntries={updateHubSortListEntries}
                  cache={cacheForComplete}
                  listRef={listRefComplete}
                  virtualizationIndex={index}
                />
              </div>
            );
          } else {
            return <div key={key} style={style} />;
          }
        },
      [
        sortListBundles.bundlesWithIssues,
        updateHubSortListEntries,
        cacheForIncomplete,
        filteredBundles,
      ]
    );
    const listHeight = {
      height: `${
        filterSectionRef.current
          ? `calc(100% - ${filterSectionRef.current.offsetHeight}px)`
          : ''
      }`,
    };

    return (
      <div className="sort-list__wrapper">
        <div
          className="sort-list__page__filter sort-list__filter"
          ref={filterSectionRef}
        >
          <ControlSortListFilters
            allSenders={allSenders}
            selectedSenders={selectedSenders}
            filterInputValue={filterInputValue}
            inputRef={inputRef}
            onCheckboxChange={handleCheckboxChange}
            onFilterInputChange={onChangeInput}
            onSetSelectedSenders={setSelectedSenders}
          />
        </div>
        <div style={listHeight}>
          <TabsComponent
            variant="fullWidth"
            scrollButtons="auto"
            onChange={(ev, value) => setSelectedTab(value)}
            tabsChildren={[
              {
                tabClassName: 'sort-table-tab',
                contentClassName: 'sort-table-tab-content',
                disabled: false,
                label: 'Incomplete Bundles',
                tabValue: 'incomplete',
                tabPanelNode: (
                  <div className="virtualization-wrapper">
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          height={height}
                          width={width}
                          rowCount={
                            selectedTab === 'incomplete' &&
                            filteredBundles &&
                            filteredBundles.filteredBundlesWithIssues
                              ? filteredBundles.filteredBundlesWithIssues.length
                              : sortListBundles.bundlesWithIssues.length
                          }
                          deferredMeasurementCache={cacheForIncomplete}
                          rowHeight={cacheForIncomplete?.rowHeight || 50}
                          rowRenderer={sortListIncompleteTableWrapper}
                          overscanRowCount={4}
                          ref={listComponent =>
                            (listRefIncomplete = listComponent)
                          }
                        />
                      )}
                    </AutoSizer>
                  </div>
                ),
              },
              {
                tabClassName: 'sort-table-tab',
                contentClassName: 'sort-table-tab-content',
                disabled: false,
                label: 'Completed Bundles',
                tabValue: 'complete',
                tabPanelNode: (
                  <div className="virtualization-wrapper">
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          height={height}
                          width={width}
                          rowCount={
                            selectedTab === 'complete' &&
                            filteredBundles.filteredBundlesCompleted
                              ? filteredBundles.filteredBundlesCompleted.length
                              : sortListBundles.bundlesCompleted.length
                          }
                          deferredMeasurementCache={cacheForComplete}
                          rowHeight={cacheForComplete?.rowHeight || 50}
                          rowRenderer={sortListCompleteTableWrapper}
                          overscanRowCount={4}
                          ref={listComponent =>
                            (listRefComplete = listComponent)
                          }
                        />
                      )}
                    </AutoSizer>
                  </div>
                ),
              },
            ]}
            value={selectedTab}
          />
        </div>
      </div>
    );
  },
  checkProps
);

ControlSortList.displayName = 'ControlSortList';
ControlSortList.whyDidYouRender = true;
export default ControlSortList;
